import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, Star, Eye, MoreVertical } from 'lucide-react';
import { getAdminComics, deleteAdminComic, featureComic } from '../../services/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';

export function AdminComics() {
  const [comics, setComics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchComics();
  }, [page]);

  const fetchComics = async () => {
    try {
      setLoading(true);
      const res = await getAdminComics({ page, limit: 20 });
      setComics(res.data.comics || res.data);
      setTotalPages(res.data.pages || 1);
    } catch (error) {
      console.error('Error fetching comics:', error);
      toast.error('Failed to load comics');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (comicId) => {
    if (!window.confirm('Are you sure you want to delete this comic?')) return;
    try {
      await deleteAdminComic(comicId);
      toast.success('Comic deleted');
      fetchComics();
    } catch (error) {
      toast.error('Failed to delete comic');
    }
  };

  const handleFeature = async (comicId, featured) => {
    try {
      await featureComic(comicId, featured);
      toast.success(featured ? 'Comic featured' : 'Comic unfeatured');
      fetchComics();
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const filteredComics = comics.filter(comic =>
    comic.title?.toLowerCase().includes(search.toLowerCase()) ||
    comic.creator_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="admin-comics">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-teko">Comics Management</h2>
        <span className="text-sm text-[var(--text-secondary)]">{comics.length} comics</span>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" size={18} />
        <input
          type="text"
          placeholder="Search comics..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
        />
      </div>

      {/* Comics Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {[...Array(10)].map((_, i) => (
            <div key={`comic-skeleton-${i}`} className="bg-[var(--surface)] rounded-xl animate-pulse">
              <div className="aspect-[3/4] bg-[var(--border)] rounded-t-xl" />
              <div className="p-3 space-y-2">
                <div className="h-4 w-3/4 bg-[var(--border)] rounded" />
                <div className="h-3 w-1/2 bg-[var(--border)] rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filteredComics.map(comic => (
            <div 
              key={comic.id} 
              className="bg-[var(--surface)] border border-[var(--border)] rounded-xl overflow-hidden group"
            >
              <div className="relative aspect-[3/4]">
                <img 
                  src={comic.cover_image} 
                  alt={comic.title}
                  className="w-full h-full object-cover"
                />
                {comic.is_featured && (
                  <div className="absolute top-2 left-2 p-1 bg-yellow-500 rounded">
                    <Star size={14} className="text-black" fill="black" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleFeature(comic.id, !comic.is_featured)}
                  >
                    <Star size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(comic.id)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
              <div className="p-3">
                <h3 className="font-medium text-sm line-clamp-1">{comic.title}</h3>
                <p className="text-xs text-[var(--text-secondary)]">{comic.creator_name}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-[var(--text-secondary)]">
                  <span className="flex items-center gap-1">
                    <Eye size={12} />
                    {comic.views?.toLocaleString()}
                  </span>
                  <span>{comic.episode_count || 0} eps</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="px-4 py-2">Page {page} of {totalPages}</span>
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

export default AdminComics;
